import { useEffect, useState, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ProductMaterialTemplatesTab } from "./ProductMaterialTemplatesTab";

const productSchema = z.object({
  sku: z.string().optional(),
  name: z.string().min(1, "Nimi on pakollinen"),
  category: z.string().optional(),
  description: z.string().optional(),
  unit: z.string().optional(),
  price: z.string().min(1, "Yksikköhinta on pakollinen"),
  vat_rate: z.string().min(1, "ALV% on pakollinen"),
  price_includes_vat: z.boolean().default(false),
  is_active: z.boolean().default(true),
  image_url: z.string().optional(),
});

type ProductFormValues = z.infer<typeof productSchema>;

export interface ProductData {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  description: string | null;
  unit: string | null;
  price: number;
  vat_rate: number;
  price_includes_vat: boolean;
  is_active: boolean;
  image_url: string | null;
}

interface ProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: ProductData | null;
}

export function ProductDialog({ open, onOpenChange, product }: ProductDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = !!product;

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      sku: "",
      name: "",
      category: "",
      description: "",
      unit: "",
      price: "",
      vat_rate: "25.50",
      price_includes_vat: false,
      is_active: true,
      image_url: "",
    },
  });

  // Watch price, vat_rate and price_includes_vat for real-time calculation
  const watchedPrice = useWatch({ control: form.control, name: "price" });
  const watchedVatRate = useWatch({ control: form.control, name: "vat_rate" });
  const watchedPriceIncludesVat = useWatch({ control: form.control, name: "price_includes_vat" });

  // Calculate the other price (net or gross) based on the input
  const calculatedPrice = useMemo(() => {
    const price = parseFloat(watchedPrice) || 0;
    const vatRate = parseFloat(watchedVatRate) || 0;
    
    if (price === 0) return null;
    
    if (watchedPriceIncludesVat) {
      // Price includes VAT, calculate net price
      const netPrice = price / (1 + vatRate / 100);
      return { label: "Veroton", value: netPrice };
    } else {
      // Price is net, calculate gross price
      const grossPrice = price * (1 + vatRate / 100);
      return { label: "Verollinen", value: grossPrice };
    }
  }, [watchedPrice, watchedVatRate, watchedPriceIncludesVat]);

  // Nollaa mutaatiot kun dialog suljetaan
  useEffect(() => {
    if (!open) {
      createMutation.reset();
      updateMutation.reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (product) {
      form.reset({
        sku: product.sku || "",
        name: product.name || "",
        category: product.category || "",
        description: product.description || "",
        unit: product.unit || "",
        price: product.price?.toString() || "",
        vat_rate: product.vat_rate?.toString() || "25.50",
        price_includes_vat: product.price_includes_vat ?? false,
        is_active: product.is_active ?? true,
        image_url: product.image_url || "",
      });
    } else {
      form.reset({
        sku: "",
        name: "",
        category: "",
        description: "",
        unit: "",
        price: "",
        vat_rate: "25.50",
        price_includes_vat: false,
        is_active: true,
        image_url: "",
      });
    }
  }, [product, form]);

  const createMutation = useMutation({
    mutationFn: async (values: ProductFormValues) => {
      const { error } = await supabase.from("products").insert({
        sku: values.sku || null,
        name: values.name,
        category: values.category || null,
        description: values.description || null,
        unit: values.unit || null,
        price: parseFloat(values.price),
        vat_rate: parseFloat(values.vat_rate),
        price_includes_vat: values.price_includes_vat,
        is_active: values.is_active,
        image_url: values.image_url || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Tuote luotu onnistuneesti");
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: ProductFormValues) => {
      const { error } = await supabase
        .from("products")
        .update({
          sku: values.sku || null,
          name: values.name,
          category: values.category || null,
          description: values.description || null,
          unit: values.unit || null,
          price: parseFloat(values.price),
          vat_rate: parseFloat(values.vat_rate),
          price_includes_vat: values.price_includes_vat,
          is_active: values.is_active,
          image_url: values.image_url || null,
        })
        .eq("id", product!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Tuote päivitetty onnistuneesti");
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
    },
  });

  const onSubmit = (values: ProductFormValues) => {
    if (isEditing) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Muokkaa tuotetta" : "Luo uusi tuote"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Muokkaa tuotteen tietoja alla olevassa lomakkeessa."
              : "Täytä tuotteen tiedot alla olevaan lomakkeeseen."}
          </DialogDescription>
        </DialogHeader>

        {isEditing ? (
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Perustiedot</TabsTrigger>
              <TabsTrigger value="materials">Materiaalit</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="sku"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SKU</FormLabel>
                          <FormControl>
                            <Input placeholder="esim. TB-001" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Kategoria</FormLabel>
                          <FormControl>
                            <Input placeholder="esim. Hautakivet" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nimi *</FormLabel>
                        <FormControl>
                          <Input placeholder="Tuotteen nimi" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Kuvaus</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tuotteen kuvaus"
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Kuvan URL</FormLabel>
                        <FormControl>
                          <Input placeholder="https://..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="price_includes_vat"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>Hinnan syöttötapa</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => field.onChange(value === "true")}
                            value={field.value ? "true" : "false"}
                            className="flex gap-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="false" id="price-net" />
                              <Label htmlFor="price-net">Veroton hinta</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="true" id="price-gross" />
                              <Label htmlFor="price-gross">Verollinen hinta</Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="unit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Yksikkö</FormLabel>
                          <FormControl>
                            <Input placeholder="esim. kpl, m, h" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {watchedPriceIncludesVat ? "Verollinen hinta (€) *" : "Veroton hinta (€) *"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              {...field}
                            />
                          </FormControl>
                          {calculatedPrice && (
                            <p className="text-sm text-muted-foreground">
                              {calculatedPrice.label}: {calculatedPrice.value.toFixed(2)} €
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="vat_rate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ALV % *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="25.50"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Aktiivinen</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            Aktiiviset tuotteet näkyvät tilauslomakkeessa
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end gap-4 border-t pt-4 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                    >
                      Peruuta
                    </Button>
                    <Button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? "Tallennetaan..."
                        : "Tallenna muutokset"}
                    </Button>
                  </div>
                </form>
              </Form>
            </TabsContent>

            <TabsContent value="materials">
              <ProductMaterialTemplatesTab productId={product.id} />
            </TabsContent>
          </Tabs>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="sku"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SKU</FormLabel>
                      <FormControl>
                        <Input placeholder="esim. TB-001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kategoria</FormLabel>
                      <FormControl>
                        <Input placeholder="esim. Hautakivet" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nimi *</FormLabel>
                    <FormControl>
                      <Input placeholder="Tuotteen nimi" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kuvaus</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tuotteen kuvaus"
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="image_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kuvan URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price_includes_vat"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Hinnan syöttötapa</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) => field.onChange(value === "true")}
                        value={field.value ? "true" : "false"}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="false" id="price-net-new" />
                          <Label htmlFor="price-net-new">Veroton hinta</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="true" id="price-gross-new" />
                          <Label htmlFor="price-gross-new">Verollinen hinta</Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Yksikkö</FormLabel>
                      <FormControl>
                        <Input placeholder="esim. kpl, m, h" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {watchedPriceIncludesVat ? "Verollinen hinta (€) *" : "Veroton hinta (€) *"}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                        />
                      </FormControl>
                      {calculatedPrice && (
                        <p className="text-sm text-muted-foreground">
                          {calculatedPrice.label}: {calculatedPrice.value.toFixed(2)} €
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vat_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ALV % *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="25.50"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Aktiivinen</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        Aktiiviset tuotteet näkyvät tilauslomakkeessa
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-4 border-t pt-4 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Peruuta
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Tallennetaan..."
                    : "Luo tuote"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
